import { useEffect, useMemo, useState } from 'react';
import { useInventoryAppServices } from '@/app/services/InventoryAppServicesContext';
import { useStartupSession } from '@/app/session/StartupSessionContext';
import {
  EnmonMeterFilesClient,
  type EnmonMeterPhotoLink,
  isMongoObjectId,
} from '@/features/inventorization/enmon-meter-files-client';
import { useLocationBootstrap } from '@/features/location-bootstrap/LocationBootstrapContext';
import type { CaptureSession, LocalPhotoRef } from '@/shared/capture/capture-session';

export function useExistingMeterPhotos(session: CaptureSession) {
  const { activeLocation } = useStartupSession();
  const { bootstrap } = useLocationBootstrap();
  const client = useMemo(() => new EnmonMeterFilesClient(), []);
  const meterId = readExistingMeterObjectId(session, bootstrap?.existingMeters);
  const [state, setState] = useState<{
    error: string | null;
    isLoading: boolean;
    photos: EnmonMeterPhotoLink[];
  }>({
    error: null,
    isLoading: false,
    photos: [],
  });

  useEffect(() => {
    if (!(activeLocation?.apiKey && meterId)) {
      setState({ error: null, isLoading: false, photos: [] });
      return;
    }

    let isMounted = true;

    setState(current => ({ ...current, error: null, isLoading: true }));
    void client
      .loadMeterPhotoLinks({
        locationApiKey: activeLocation.apiKey,
        meterId,
      })
      .then(photos => {
        if (isMounted) {
          setState({ error: null, isLoading: false, photos });
        }
      })
      .catch(error => {
        if (isMounted) {
          setState({
            error: error instanceof Error ? error.message : 'Unable to load uploaded meter photos.',
            isLoading: false,
            photos: [],
          });
        }
      });

    return () => {
      isMounted = false;
    };
  }, [activeLocation?.apiKey, client, meterId]);

  return state;
}

export function useReviewPhotoUrls(photos: LocalPhotoRef[]) {
  const { photoBlobs: repository } = useInventoryAppServices();
  const [urls, setUrls] = useState<Record<string, string>>({});
  const photoSnapshot = JSON.stringify(
    photos.map(photo => ({
      id: photo.id,
      blobId: photo.localBlobId,
    })),
  );

  useEffect(() => {
    let isMounted = true;
    const objectUrls: string[] = [];
    const photoInputs = JSON.parse(photoSnapshot) as Array<{ blobId: string; id: string }>;

    async function loadUrls() {
      const entries = await Promise.all(
        photoInputs.map(async photo => {
          const blobRecord = await repository.get(photo.blobId);
          if (!isMounted) {
            return [photo.id, ''] as const;
          }

          const objectUrl = blobRecord ? URL.createObjectURL(blobRecord.blob) : '';

          if (objectUrl) {
            objectUrls.push(objectUrl);
          }

          return [photo.id, objectUrl] as const;
        }),
      );

      if (isMounted) {
        setUrls(Object.fromEntries(entries));
      }
    }

    void loadUrls();

    return () => {
      isMounted = false;
      for (const objectUrl of objectUrls) {
        URL.revokeObjectURL(objectUrl);
      }
    };
  }, [photoSnapshot, repository]);

  return urls;
}

export function useLocalPhotoBlob(photo: LocalPhotoRef | undefined) {
  const { photoBlobs: repository } = useInventoryAppServices();
  const [blob, setBlob] = useState<{ blob: Blob; blobId: string }>();
  const blobId = photo?.localBlobId;

  useEffect(() => {
    if (!blobId) {
      return;
    }

    let isMounted = true;
    void repository
      .get(blobId)
      .then(record => {
        if (isMounted && record) {
          setBlob({ blob: record.blob, blobId });
        }
      })
      .catch(() => undefined);

    return () => {
      isMounted = false;
    };
  }, [blobId, repository]);

  return blob?.blobId === blobId ? blob?.blob : undefined;
}

function readExistingMeterObjectId(session: CaptureSession, meters: Array<{ _id: string; id: number }> = []) {
  if (isMongoObjectId(session._id)) {
    return session._id;
  }

  const matchedMeter = meters.find(meter => meter.id === session.meterId);

  if (!matchedMeter) {
    return undefined;
  }

  return isMongoObjectId(matchedMeter._id) ? matchedMeter._id : undefined;
}
